export const toDdmmyyyy = (dateStr: string | null | undefined): string => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '-') return dateStr || '-';
    const clean = dateStr.trim();
    // If it's already DD-MM-YYYY, return it.
    const alreadyDdmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.test(clean);
    if (alreadyDdmmyyyy) return clean;

    // DD/MM/YYYY (slash-separated) — same layout as DD-MM-YYYY, just a different
    // separator (some sources, e.g. OCR-extracted receipts, use this). new Date()
    // would otherwise misread this as MM/DD/YYYY, so handle it explicitly.
    const ddmmyyyySlash = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(clean);
    if (ddmmyyyySlash) {
        return `${ddmmyyyySlash[1]}-${ddmmyyyySlash[2]}-${ddmmyyyySlash[3]}`;
    }

    // Check if it's YYYY-MM-DD
    const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean);
    if (yyyymmdd) {
        return `${yyyymmdd[3]}-${yyyymmdd[2]}-${yyyymmdd[1]}`;
    }

    // Try parsing using JS Date
    const parsed = new Date(clean);
    if (!isNaN(parsed.getTime())) {
        const d = String(parsed.getDate()).padStart(2, '0');
        const m = String(parsed.getMonth() + 1).padStart(2, '0');
        const y = parsed.getFullYear();
        return `${d}-${m}-${y}`;
    }
    return dateStr;
};

export const formatDateToDdmmyyyy = (date: Date | string | number | null | undefined): string => {
    if (!date) return '-';
    try {
        if (typeof date === 'string') {
            const clean = date.trim();
            // Match YYYY-MM-DD (possibly followed by time or T...)
            const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/.exec(clean);
            if (match) {
                return `${match[3]}-${match[2]}-${match[1]}`;
            }
            // Match DD-MM-YYYY
            if (/^\d{2}-\d{2}-\d{4}$/.test(clean)) {
                return clean;
            }
        }
        const dObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dObj.getTime())) return String(date);
        const d = String(dObj.getDate()).padStart(2, '0');
        const m = String(dObj.getMonth() + 1).padStart(2, '0');
        const y = dObj.getFullYear();
        return `${d}-${m}-${y}`;
    } catch {
        return String(date);
    }
};

// The dashboard's one date-and-time format: "DD-MM-YYYY • h:mm AM", in the viewer's
// local time. Takes a Date, epoch ms or a parseable string.
export const formatDateTimeDdmmyyyy = (date: Date | string | number | null | undefined): string => {
    if (date === null || date === undefined || date === '') return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return String(date);
    const h = d.getHours() % 12 || 12;
    return `${formatDateToDdmmyyyy(d)} • ${h}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
};

// An ISO timestamp that names its zone: a trailing Z or a ±HH:MM offset.
const ZONED_ISO = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i;
const IST_OFFSET_MS = 330 * 60 * 1000;

/** The IST wall-clock form ("YYYY-MM-DDTHH:mm:ss") of a zoned timestamp. */
const toIstWallTime = (dateStr: string): string | null => {
    // JS Date keeps milliseconds only; trim longer fractions (Python writes 6).
    const ms = Date.parse(dateStr.replace(/(\.\d{3})\d+/, '$1'));
    if (isNaN(ms)) return null;
    return new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 19);
};

export const formatRawDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '-') return dateStr || '-';
    try {
        // A value that names its zone is shown in IST. Printing its clock as
        // written put a UTC visit window ("...T06:41:00.000Z", which is
        // 12:11 PM IST) on screen as 06:41 AM. A value with no zone is IST
        // wall time already (datetime-local inputs) and is shown as written.
        const text = ZONED_ISO.test(dateStr) ? (toIstWallTime(dateStr) ?? dateStr) : dateStr;
        // Handle ISO strings with T or space-separated strings
        const parts = text.includes('T') ? text.split('T') : text.split(' ');
        if (parts.length >= 2) {
            const date = toDdmmyyyy(parts[0]);
            const timePart = parts[1].slice(0, 8); // Take HH:mm:ss
            
            const [h, m] = timePart.split(':');
            let hour = parseInt(h);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            hour = hour % 12;
            hour = hour ? hour : 12; // the hour '0' should be '12'
            
            return `${date} • ${hour}:${m} ${ampm}`;
        }
        // If it's just a date without time
        return toDdmmyyyy(text);
    } catch {
        return dateStr;
    }
};

// Plain-string counterpart to the <MaskedMobile> component — for contexts with no room for
// an interactive reveal toggle (Excel/CSV exports, plain-text summaries). Masks everything but
// the last 4 digits, e.g. "9876543210" -> "+91 ••••••3210".
/** A mobile with its dial code ("+971 501234567"); +91 when the user has none stored. */
export const withCountryCode = (mobile: string | null | undefined, countryCode?: string | number | null): string => {
    if (!mobile) return '';
    const cc = String(countryCode ?? '').replace(/\D/g, '') || '91';
    return `+${cc} ${mobile}`;
};

export const maskMobileForExport = (mobile: string | number | null | undefined): string => {
    if (mobile === null || mobile === undefined) return '';
    const str = String(mobile).trim();
    if (!str || str === '-' || str === '—' || str === 'N/A') return str;
    if (str.length <= 4) return str;
    return `+91 ••••••${str.slice(-4)}`;
};

export const numberToWords = (num: number): string => {
    if (num === 0) return 'Zero';

    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convert = (n: number): string => {
        if (n < 20) return units[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '');
        if (n < 1000) return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convert(n % 100) : '');
        if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convert(n % 1000) : '');
        if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
        return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 !== 0 ? ' ' + convert(n % 10000000) : '');
    };

    return convert(num);
};
